class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.overlay = this.querySelector('[id^="CartDrawer-Overlay"]');
    this.overlay?.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    if (Shopify.designMode) {
      document.addEventListener('shopify:section:select', (e) => {
        if (e.target.matches('.section-cart-drawer')) this.open();
      });

      document.addEventListener('shopify:section:deselect', (e) => {
        if (e.target.matches('.section-cart-drawer')) this.close();
      });
    }
  }

  connectedCallback() {
    if (!this.dataset.moved) {
      this.dataset.moved = true;
      document.body.appendChild(this);
    }

    window.initCartDrawerHeaderHeight?.();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('.cart-icon--drawer');
    cartLink?.setAttribute('role', 'button');
    cartLink?.setAttribute('aria-haspopup', 'dialog');
    cartLink?.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink?.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  #getDrawerDirection() {
    let drawerDirection = this.getAttribute('data-drawer-direction');
    const rtl = typeof theme !== 'undefined' && theme.config && theme.config.rtl === true;

    if (rtl) {
      drawerDirection = drawerDirection === 'left' ? 'right' : 'left';
    }

    return drawerDirection;
  }

  #prepareDrawerAnimationState() {
    const contentElement = this.querySelector('[data-drawer-content]');
    if (!contentElement) return;

    contentElement.getAnimations().forEach((animation) => animation.cancel());

    const drawerDirection = this.#getDrawerDirection();
    contentElement.style.opacity = '0';
    contentElement.style.transform =
      drawerDirection === 'left' ? 'translateX(-100%)' : 'translateX(100%)';
  }

  #waitForNextFrame() {
    return new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(resolve));
    });
  }

  #bindOverlayClose() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (!overlay) return;

    const newOverlay = overlay.cloneNode(true);
    overlay.replaceWith(newOverlay);
    newOverlay.addEventListener('click', () => this.close());
  }

  open(triggeredBy, options = {}) {
    const { deferLayoutMeasure = false } = options;

    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    const contentElement = this.querySelector('[data-drawer-content]');
    const drawerDirection = this.#getDrawerDirection();

    this.classList.add('open');

    const animation = Motion.timeline([
      [
        contentElement,
        {
          opacity: [0, 1],
          transform:
            drawerDirection === 'left'
              ? ['translateX(-100%)', 'translateX(0)']
              : ['translateX(100%)', 'translateX(0)'],
        },
        { duration: 0.3, easing: [0.61, 0.22, 0.23, 1], at: '-0.05' },
      ],
    ]);

    let openedHandled = false;
    const onDrawerOpened = () => {
      if (openedHandled) return;
      openedHandled = true;

      const containerToTrapFocusOn = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
      window.initCartDrawerHeaderHeight?.();
    };

    animation.finished.then(onDrawerOpened);
    this.addEventListener('transitionend', onDrawerOpened, { once: true });

    if (!deferLayoutMeasure) {
      window.scheduleCartDrawerHeaderHeight?.();
    }

    document.body.classList.add('overflow-hidden');
document.documentElement.setAttribute('scroll-lock', '');

setTimeout(() => {
  removeDuplicateFreeShippingMessages();
  updateBavisValueProgress();
}, 100);
  }

  async close() {
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.documentElement.removeAttribute('scroll-lock');
    const cartDrawerDirection = this.getAttribute("data-drawer-direction");
    const contentElement = this.querySelector("[data-drawer-content]");

    let rtl = typeof theme !== "undefined" && theme.config && theme.config.rtl === true;
    let drawerDirection = cartDrawerDirection;
    
    if (rtl) {
      drawerDirection = drawerDirection === "left" ? "right" : "left";
    }

    await Motion.timeline([
      [
        contentElement,
        {
          opacity: [1, 0],
          transform:
            drawerDirection === "left"
              ? ["translateX(0)", "translateX(-100%)"]
              : ["translateX(0)", "translateX(100%)"],
        },
        { duration: 0.3, easing: [0.61, 0.22, 0.23, 1] },
      ]
    ]).finished;

    this.classList.remove("open");
    this.classList.remove('active');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  async renderContents(parsedState) {
    this.productId = parsedState.id;
    const layoutOptions = { measureHeight: false };
    const recommendationsWrapper = this.querySelector('.drawer__cart-items-recommendations-wrapper');
    let preservedRecommendations = recommendationsWrapper?.innerHTML?.trim() || null;
    if (preservedRecommendations) {
      const preservedDoc = new DOMParser().parseFromString(preservedRecommendations, 'text/html');
      preservedDoc.querySelectorAll('.add-to-cart-button').forEach((button) => {
        button.removeAttribute('aria-disabled');
        button.classList.remove('loading');
        button.querySelector('.loading__spinner')?.classList.add('hidden');
      });
      preservedRecommendations = preservedDoc.body.innerHTML.trim();
    }
    
    const cartDrawerItems = document.querySelector('cart-drawer-items');
    const hasDualCartDrawerLayout = !!document.querySelector('[data-cart-drawer-empty]');
    const cartDrawerSectionKey = parsedState.sections
      ? Object.keys(parsedState.sections).find((key) => key.includes('cart-drawer'))
      : null;

    await Promise.all(
      this.getSectionsToRender().map((section) => {
        if (section.id === 'cart-icon-bubble') {
          return this.updateCartIconBubble(parsedState);
        }

        if (
          section.id === 'cart-drawer' &&
          hasDualCartDrawerLayout &&
          cartDrawerSectionKey &&
          parsedState.sections[cartDrawerSectionKey] &&
          cartDrawerItems?.updateSections
        ) {
          cartDrawerItems.updateSections(parsedState.sections, parsedState, layoutOptions);
          return;
        }

        const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
        const sectionHtml =
          parsedState.sections?.[section.id] ||
          (cartDrawerSectionKey ? parsedState.sections[cartDrawerSectionKey] : null);

        if (sectionElement && sectionHtml) {
          sectionElement.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
        }
      })
    );

    if (hasDualCartDrawerLayout && parsedState.item_count !== undefined) {
      window.setCartDrawerEmptyState?.(parsedState.item_count === 0, layoutOptions);
    }

    if (preservedRecommendations) {
      const newRecommendationsWrapper = this.querySelector('.drawer__cart-items-recommendations-wrapper');
      const hasRecommendations = newRecommendationsWrapper?.querySelector('.cart-drawer__recommendations');
      if (newRecommendationsWrapper && !hasRecommendations) {
        newRecommendationsWrapper.innerHTML = preservedRecommendations;
      }
    }

    if (typeof initPurchaseConditions === 'function') {
      initPurchaseConditions(this);
    }

    this.#bindOverlayClose();
this.#prepareDrawerAnimationState();
await this.#waitForNextFrame();
this.open(null, { deferLayoutMeasure: true });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  async updateCartIconBubble(parsedState) {
    const sectionHtml = parsedState.sections?.['cart-icon-bubble'];

    if (sectionHtml) {
      document.querySelectorAll('#cart-icon-bubble').forEach((cartIconBubble) => {
        const cartTrigger = cartIconBubble.closest('button, a');
        const hasCartText = cartTrigger?.querySelector('.cart-text');

        if (!hasCartText) {
          cartIconBubble.innerHTML = this.getSectionInnerHTML(sectionHtml);
        }
      });
    }

    let itemCount = this.getItemCountFromSectionHtml(sectionHtml);

    if (itemCount === undefined && parsedState.item_count !== undefined) {
      itemCount = parsedState.item_count;
    }

    if (itemCount === undefined) {
      try {
        const cart = await fetch('/cart.js').then((response) => response.json());
        itemCount = cart.item_count;
      } catch (e) {
        console.error(e);
      }
    }

    if (itemCount !== undefined) {
      this.updateCartCounts(itemCount);
    }
  }

  getItemCountFromSectionHtml(sectionHtml) {
    if (!sectionHtml) return undefined;

    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const root = doc.querySelector('.shopify-section') || doc.body;
    const countEl = root.querySelector('[data-cart-count]');

    if (!countEl) return undefined;

    const text = countEl.textContent.trim();
    const num = parseInt(text, 10);

    if (!isNaN(num)) return num;

    if (text === window.cartStrings?.item_99) return 100;

    return undefined;
  }

  updateCartCounts(itemCount) {
    const maxLabel = window.cartStrings?.item_99;
    const countValue = itemCount < 100 ? String(itemCount) : maxLabel;

    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      if (el.closest('.cart-text')) {
        el.textContent = itemCount < 100 ? `(${itemCount})` : `(${maxLabel})`;
      } else {
        el.textContent = countValue;
      }
    });

    document.querySelectorAll('[data-cart-text]').forEach((el) => {
      if (itemCount === 1) {
        el.textContent = window.cartStrings?.item?.replace('[count]', itemCount) ?? el.textContent;
      } else {
        el.textContent = window.cartStrings?.items?.replace('[count]', itemCount) ?? el.textContent;
      }
    });

    document.querySelectorAll('button.cart-icon--drawer, a.cart-icon--drawer').forEach((trigger) => {
      const cartTextEl = trigger.querySelector('[data-cart-text]');
      if (cartTextEl) {
        trigger.setAttribute('aria-label', cartTextEl.textContent);
      }
    });

    window.updateCartEmptyStateVisibility?.(itemCount);
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}
if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);
/* =========================================================
   BAVIS VALUE SHOPPING REWARDS
   FINAL DISCOUNT STRUCTURE
   ========================================================= */
/* =========================================================
   REMOVE DUPLICATE FREE SHIPPING MESSAGE
   ========================================================= */

function removeDuplicateFreeShippingMessages() {
  const cartDrawers = document.querySelectorAll('cart-drawer');

  cartDrawers.forEach((drawer) => {
    const shippingMessages =
      drawer.querySelectorAll('.previewCartMessage');

    if (shippingMessages.length > 1) {
      shippingMessages.forEach((message, index) => {
        if (index > 0) {
          message.remove();
        }
      });
    }
  });
}
function updateBavisValueProgress() {
  const progress = document.getElementById('BavisValueProgress');

  if (!progress) return;

  fetch('/cart.js', {
    method: 'GET',
    headers: {
      'Accept': 'application/json'
    },
    cache: 'no-store'
  })
    .then((response) => response.json())
    .then((cart) => {

      const message =
        document.getElementById('BavisValueMessage');

      const fill =
        document.getElementById('BavisValueProgressFill');

      const current =
        document.getElementById('BavisValueCurrent');

      const target =
        document.getElementById('BavisValueTarget');

      if (!message || !fill || !current || !target) return;


      /*
       * Shopify returns money values in paise.
       *
       * items_subtotal_price is used so that the
       * reward is calculated from the cart value
       * BEFORE the value-based discount.
       */
      const cartTotal =
        cart.items_subtotal_price / 100;


      /*
       * FINAL BAVIS VALUE SHOPPING LEVELS
       */
      const levels = [
        {
          amount: 999,
          reward: 'FREE SHIPPING'
        },
        {
          amount: 2000,
          reward: '₹50 OFF'
        },
        {
          amount: 3000,
          reward: '₹100 OFF'
        },
        {
          amount: 4000,
          reward: '₹150 OFF'
        },
        {
          amount: 5000,
          reward: '₹200 OFF'
        },
        {
          amount: 6000,
          reward: '₹250 OFF'
        },
        {
          amount: 7000,
          reward: '₹300 OFF'
        },
        {
          amount: 8000,
          reward: '₹350 OFF'
        },
        {
          amount: 9000,
          reward: '₹400 OFF'
        },
        {
          amount: 10000,
          reward: '5% OFF'
        }
      ];


      /*
       * Find the next reward level.
       */
      const nextLevel = levels.find(
        (level) => cartTotal < level.amount
      );


      /*
       * =====================================================
       * ₹10,000+ — 5% OFF
       * =====================================================
       *
       * There is NO upper limit.
       */
      if (!nextLevel) {

        const discount =
          cartTotal * 0.05;

        current.textContent =
          '₹' +
          Math.round(cartTotal).toLocaleString('en-IN');

        target.textContent =
          '5% OFF';

        message.innerHTML =
          '🏆 <strong>5% OFF unlocked!</strong> ' +
          'You save <strong>₹' +
          Math.round(discount).toLocaleString('en-IN') +
          '</strong> on this order.';

        fill.style.width = '100%';

        progress.classList.add(
          'bavis-value-progress--complete'
        );

        return;
      }


      /*
       * Find the previous reward level.
       */
      const nextIndex =
        levels.indexOf(nextLevel);

      const previousLevel =
        nextIndex > 0
          ? levels[nextIndex - 1]
          : {
              amount: 0,
              reward: null
            };


      /*
       * Amount remaining to unlock
       * the next reward.
       */
      const amountRemaining =
        nextLevel.amount - cartTotal;


      /*
       * Calculate progress within the
       * current reward level.
       */
      const tierStart =
        previousLevel.amount;

      const tierEnd =
        nextLevel.amount;

      let progressPercent =
        ((cartTotal - tierStart) /
          (tierEnd - tierStart)) * 100;


      /*
       * Keep progress between 0 and 100%.
       */
      progressPercent =
        Math.max(
          0,
          Math.min(
            100,
            progressPercent
          )
        );


      /*
       * Current cart value.
       */
      current.textContent =
        '₹' +
        Math.round(cartTotal).toLocaleString('en-IN');


      /*
       * Next reward target.
       */
      target.textContent =
        '₹' +
        nextLevel.amount.toLocaleString('en-IN');


      /*
       * Progress bar.
       */
      fill.style.width =
        progressPercent + '%';


      /*
       * =====================================================
       * BELOW ₹999
       * =====================================================
       */
      if (cartTotal < 999) {

        message.innerHTML =
          '🛍️ Add <strong>₹' +
          Math.ceil(amountRemaining)
            .toLocaleString('en-IN') +
          '</strong> more to get ' +
          '<strong>FREE SHIPPING!</strong>';

      }


      /*
       * =====================================================
       * ₹999 – ₹1,999
       * =====================================================
       */
      else if (cartTotal < 2000) {

        message.innerHTML =
          '🚚 <strong>FREE SHIPPING unlocked!</strong> ' +
          'Add <strong>₹' +
          Math.ceil(amountRemaining)
            .toLocaleString('en-IN') +
          '</strong> more to get ' +
          '<strong>₹50 OFF</strong>.';

      }


      /*
       * =====================================================
       * ₹2,000+
       * =====================================================
       */
      else {

        message.innerHTML =
          '🎉 <strong>' +
          getBavisUnlockedReward(cartTotal) +
          '</strong> unlocked! ' +
          'Add <strong>₹' +
          Math.ceil(amountRemaining)
            .toLocaleString('en-IN') +
          '</strong> more for ' +
          '<strong>' +
          nextLevel.reward +
          '</strong>.';

      }


      /*
       * Remove completed state if
       * customer is below ₹10,000.
       */
      progress.classList.remove(
        'bavis-value-progress--complete'
      );

    })
    .catch((error) => {

      console.error(
        'Bavis Value Shopping Progress Error:',
        error
      );

    });
}


/* =========================================================
   RETURN CURRENTLY UNLOCKED REWARD
   ========================================================= */

function getBavisUnlockedReward(cartTotal) {

  if (cartTotal >= 10000)
    return '5% OFF';

  if (cartTotal >= 9000)
    return '₹400 OFF';

  if (cartTotal >= 8000)
    return '₹350 OFF';

  if (cartTotal >= 7000)
    return '₹300 OFF';

  if (cartTotal >= 6000)
    return '₹250 OFF';

  if (cartTotal >= 5000)
    return '₹200 OFF';

  if (cartTotal >= 4000)
    return '₹150 OFF';

  if (cartTotal >= 3000)
    return '₹100 OFF';

  if (cartTotal >= 2000)
    return '₹50 OFF';

  if (cartTotal >= 999)
    return 'FREE SHIPPING';

  return '';
}
class ModalComponent extends HTMLElement {
  constructor() {
    super();
    this.querySelector('#Modal-Overlay')?.addEventListener('click', this.close.bind(this));

    const modalId = `#${this.id}`;
    const toggleButtons = document.querySelectorAll(`[data-drawer-toggle="${modalId}"]`);

    toggleButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.isOpen ? this.close() : this.show(button);
      });
    });

    // Shopify Design Mode
    if (Shopify.designMode && this.hasAttribute('check-shopify-design-mode')) {
      this.addEventListener('shopify:block:select', () => (this.isOpen = true));
      this.addEventListener('shopify:block:deselect', () => (this.isOpen = false));
    }
  }

  connectedCallback() {
    if (!this.dataset.moved) {
      this.dataset.moved = true;
      document.body.appendChild(this);
    }
  }

  static get observedAttributes() {
    return ['open'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'open') {
      this.setAttribute('aria-expanded', newValue === '' ? 'true' : 'false');
    }
  }

  get isOpen() {
    return this._isOpen;
  }

  set isOpen(value) {
    if (value !== this._isOpen) {
      this._isOpen = value;
      if (this.isConnected) {
        this._animate(value);
      } else {
        value ? this.setAttribute('open', '') : this.removeAttribute('open');
      }
    }
    this.setAttribute('aria-expanded', value ? 'true' : 'false');
  }

  show(opener) {
    this.openedBy = opener;
    this.isOpen = true;
    this.trapFocus();
  }

  close() {
    this.isOpen = false;
    removeTrapFocus(this.openedBy);
  }

  async _animate(open) {
    const inner = this.querySelector('.modal__inner');
    const content = this.querySelector('.modal-content');
    const header = this.querySelector('.modal-header');
    this.style.overflow = 'hidden';

    if (open) {
      this.setAttribute('open', '');
      await Motion.timeline([
        [inner, { opacity: [0, 1], transform: ['translateY(2rem)', 'translateY(0)'] }, { duration: 0.4, easing: 'cubic-bezier(0.7, 0, 0.3, 1)' }],
        [content, { opacity: [0, 1], transform: ['translateY(1rem)', 'translateY(0)'] }, { duration: 0.3, at: '-0.15' }],
        [header, { opacity: [0, 1], transform: ['translateY(-0.5rem)', 'translateY(0)'] }, { duration: 0.3, at: '-0.2' }],
      ]).finished;
    } else {
      await Motion.timeline([
        [header, { opacity: [1, 0], transform: ['translateY(0)', 'translateY(-0.5rem)'] }, { duration: 0.2 }],
        [content, { opacity: [1, 0], transform: ['translateY(0)', 'translateY(1rem)'] }, { duration: 0.2 }],
        [inner, { opacity: [1, 0], transform: ['translateY(0)', 'translateY(2rem)'] }, { duration: 0.25, easing: 'cubic-bezier(0.7, 0, 0.3, 1)' }],
      ]).finished;
      this.removeAttribute('open');
    }

    this.style.overflow = 'visible';
  }

  trapFocus() {
    this.addEventListener(
      'transitionend',
      () => {
        const container = this.querySelector('.modal__inner');
        const focusEl = this.querySelector('.modal__close');
        trapFocus(container, focusEl);
      },
      { once: true }
    );
  }
}

if (!customElements.get('modal-component')) {
  customElements.define('modal-component', ModalComponent);
}
